import { useEffect, useState, useLayoutEffect, useCallback, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { TOPBAR_COLORS } from '@/components/topbar';

interface BellNotificationBubbleProps {
  message: string;
  onClose: () => void;
  bellRef: RefObject<HTMLElement | null>;
  isMobile: boolean;
  duration?: number;
}

/**
 * BellNotificationBubble Component
 * Positions itself relative to the bell element — below on desktop, above on mobile.
 * Auto-closes after duration (default 5s).
 */
export function BellNotificationBubble({ message, onClose, bellRef, isMobile, duration = 5000 }: BellNotificationBubbleProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; arrowLeft: number } | null>(null);

  const measure = useCallback(() => {
    const el = bellRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const bellCenterX = rect.left + rect.width / 2;
    const bubbleWidth = Math.min(220, window.innerWidth - 32);
    // Center bubble on bell, but clamp to viewport
    let left = bellCenterX - bubbleWidth / 2;
    left = Math.max(16, Math.min(left, window.innerWidth - bubbleWidth - 16));
    // Arrow points at bell center
    const arrowLeft = Math.max(16, Math.min(bellCenterX - left, bubbleWidth - 16));

    if (isMobile) {
      // Bell is in bottom nav — show bubble above it
      const bottom = window.innerHeight - rect.top + 8;
      setPos({ bottom, left, arrowLeft });
    } else {
      // Bell is in top bar — show bubble below it
      const top = rect.bottom + 8;
      setPos({ top, left, arrowLeft });
    }
  }, [bellRef, isMobile]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!pos) return null;

  const slideDir = isMobile ? 'Up' : 'Down';

  return createPortal(
    <>
      <style>{`
        @keyframes bellBubbleSlideIn${slideDir} {
          0%   { transform: translateY(${isMobile ? '20px' : '-20px'}) scale(0.8); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes bellBubbleSlideOut${slideDir} {
          0%   { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(${isMobile ? '20px' : '-20px'}) scale(0.8); opacity: 0; }
        }
      `}</style>

      <div
        className="fixed z-[9999]"
        style={{
          ...(pos.top != null ? { top: pos.top } : {}),
          ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
          left: pos.left,
          animation: isVisible
            ? `bellBubbleSlideIn${slideDir} 0.3s ease-out forwards`
            : `bellBubbleSlideOut${slideDir} 0.3s ease-in forwards`,
        }}
      >
        {/* Arrow above bubble (desktop — arrow points up) */}
        {!isMobile && (
          <div
            className="absolute"
            style={{
              top: -12,
              left: pos.arrowLeft - 12,
              width: 0,
              height: 0,
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderBottom: '12px solid rgba(255, 255, 255, 0.5)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 4,
                left: -8,
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid rgba(255, 255, 255, 0.92)',
              }}
            />
          </div>
        )}

        {/* Bubble */}
        <div
          className="relative px-3 py-1.5 rounded-xl"
          style={{
            maxWidth: 220,
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(20px) saturate(150%)',
            WebkitBackdropFilter: 'blur(20px) saturate(150%)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.06)',
            color: TOPBAR_COLORS.text,
          }}
        >
          <p
            className="text-xs font-semibold"
            style={{ fontFamily: 'var(--font-sans)', lineHeight: '1.4' }}
          >
            {message}
          </p>
        </div>

        {/* Arrow below bubble (mobile — arrow points down) */}
        {isMobile && (
          <div
            className="absolute"
            style={{
              bottom: -12,
              left: pos.arrowLeft - 12,
              width: 0,
              height: 0,
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderTop: '12px solid rgba(255, 255, 255, 0.5)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: 4,
                left: -8,
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid rgba(255, 255, 255, 0.92)',
              }}
            />
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
